import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Pencil, Plus } from "lucide-react";
import {
  isListingAgentAlreadyOnFile,
  LISTING_AGENT_MATCH_FIELDS,
  LISTING_AGENT_PHONE_TYPES,
  listingsApi,
  type ListingAgentDto,
  type ListingAgentDuplicateCheckDto,
  type ListingAgentListParams,
  type ListingAgentPhoneType,
  type UpsertListingAgentPayload,
} from "@/api/buyers";
import {
  Button,
  EmptyState,
  FormField,
  Input,
  Skeleton,
  StatusBadge,
  SubTable,
  TableValue,
  Textarea,
  useToast,
  type SubTableColumn,
} from "@/design-system/components/ui";
import { Select } from "@/components/ui/Select";
// Modal is a staff-shell component, not part of the shared portal kit.
import { Modal } from "@/components/ui/Modal";
import { buyerSubmissionKeys, listingKeys } from "@/hooks/buyers/buyerKeys";
import { AgentSummary } from "@/components/buyers/ListingAgentPicker";
import { useCanUseWhosCalling, WhosCallingPanel } from "@/components/buyers/WhosCallingPanel";
import { emailFieldError } from "@/lib/emailValidator";
import { phoneFieldError } from "@/lib/phoneValidator";
import { US_STATES } from "@/lib/usStates";
import { AgentPasteToFill, type AgentPasteChoice } from "./listing/agentPaste/AgentPasteToFill";
import { appendToNotes } from "./listing/agentPaste/mergePaste";
import {
  choosePasted,
  keepTyped,
  markerTexts,
  NO_PASTE,
  pasteAfterEdit,
  pasteIntoForm,
  type PasteFormState,
} from "./listing/agentPaste/pasteFormState";
import { PasteReview } from "./listing/agentPaste/PasteReview";

/**
 * Listing agents — the SHARED registry, and the editor for one record.
 *
 * <p>Named <b>Directory</b>, not <code>ListingAgentsPanel</code>: the detail
 * page already renders <code>ListingAgentPanel</code> (singular), the read-only
 * block for the agent on ONE listing. A name differing by one letter for a
 * cross-listing registry is a trap for whoever edits next, so the two are named
 * for what they actually are.</p>
 *
 * <p>🔴 <b>The two verbs are not interchangeable.</b> <code>upsertAgent</code>
 * (POST) creates. Called plainly it GET-OR-CREATES by email and <i>fills blanks
 * only</i>, because its paste and import callers have no idea whether they have
 * met this agent before. The New agent form here calls it STRICTLY
 * (<code>rejectExisting</code>): it checks for an agent already on file first,
 * and the create either inserts or refuses with a 409, so it never reuses an
 * agent or fills in its blanks. <code>updateAgent</code> (PATCH by id) is the
 * edit verb, with null=unchanged / ""=clear overwrite semantics. Routing an edit
 * through the upsert would silently DROP the specialist's correction, so create
 * uses one and edit uses the other.</p>
 *
 * <p>🔴 <b>There is deliberately no listing-agent DETAIL PAGE, and this is why
 * the registry has to carry its own weight.</b> `listing_agent` is absent from
 * every cross-cutting registry — Notes, Tasks, Documents, Communications,
 * Messages, Activity — it has no assignee and no lifecycle beyond the one-way
 * escalation flag, and once escalated the work moves to a `BrokerLead`, which
 * already has a page. So a specialist's only route to "which agents do we have,
 * and which of them are a problem" is this modal: a dead search box or an
 * unreachable filter here has nowhere else to be answered.</p>
 */
export function ListingAgentDirectoryModal({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const [search, setSearch] = useState("");
  const [escalated, setEscalated] = useState<EscalatedFilter>("");
  const [missingEmailOnly, setMissingEmailOnly] = useState(false);
  const [editing, setEditing] = useState<ListingAgentDto | null>(null);
  const [creating, setCreating] = useState(false);
  // Addendum B (B3): an agent has no page of their own, and a row opens only
  // the Edit form, so VIEW DEALS opens the Who's-calling panel on this agent.
  const [dealsFor, setDealsFor] = useState<ListingAgentDto | null>(null);
  const canViewDeals = useCanUseWhosCalling();

  // 🔴 `q`, not `search`. The server binds every paged endpoint's free-text term
  // from `PagedQuery.Q`; the parameter this box used to send was not bound at
  // all, so typing into it filtered nothing and quietly returned the unfiltered
  // first page. See `ListingAgentListParams`.
  //
  // `escalated` was already accepted server-side and had no control anywhere —
  // "which of our agents are in broker recruitment" is exactly the question this
  // registry exists to answer, and it was unanswerable.
  const params = useMemo<ListingAgentListParams>(
    () => ({
      page: 1,
      pageSize: 100,
      q: search.trim() || undefined,
      escalated: escalated === "" ? undefined : escalated === "yes",
    }),
    [search, escalated],
  );

  const agents = useQuery({
    queryKey: listingKeys.agents(params),
    queryFn: () => listingsApi.listAgents(params),
    enabled: open,
  });

  const loaded = agents.data?.data ?? [];
  const total = agents.data?.totalCount ?? loaded.length;
  /*
    Missing-email is filtered CLIENT-side, over the page already on screen. The
    server has no `hasEmail` predicate and adding one is a different workstream's
    file; what makes the client-side version honest rather than a lie is the
    truncation caption below — a filter that silently searched 100 of 400 rows
    would report "none missing an email" about a registry full of them.
  */
  const rows = missingEmailOnly ? loaded.filter(hasNoEmail) : loaded;
  const truncated = total > loaded.length;

  return (
    <>
      <Modal
        open={open}
        onClose={onClose}
        title="Listing agents"
        description="Shared across every listing they represent — editing one here corrects it everywhere."
        size="lg"
        footer={
          <Button variant="text" onClick={onClose}>
            Done
          </Button>
        }
      >
        <div className="grid gap-space-4">
          <div className="flex flex-wrap items-end gap-space-3">
            <FormField label="Search" className="min-w-[14rem] flex-1">
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Name, email, phone, or firm"
                aria-label="Search listing agents"
              />
            </FormField>
            <FormField label="Recruitment" className="min-w-[11rem]">
              <Select
                value={escalated}
                onChange={(e) => setEscalated(e.target.value as EscalatedFilter)}
                aria-label="Filter by recruitment status"
              >
                <option value="">Any</option>
                <option value="yes">In broker recruitment</option>
                <option value="no">Not escalated</option>
              </Select>
            </FormField>
            <Button variant="secondary" onClick={() => setCreating(true)}>
              <Plus size={14} strokeWidth={1.5} aria-hidden /> New agent
            </Button>
          </div>

          {/*
            🔴 An agent with no email is a real operational problem, not a
            cosmetic gap: `upsertAgent` get-or-creates BY EMAIL, so a record
            without one is invisible to the dedupe probe and every later paste
            naming that agent mints ANOTHER row. This surfaces them so they can
            be corrected, rather than making email required at creation — a
            pasted listing that names an agent with no address is a legitimate
            thin record, and refusing it would strand the listing.
          */}
          <label className="flex items-center gap-space-2 text-small text-onyx">
            <input
              type="checkbox"
              checked={missingEmailOnly}
              onChange={(e) => setMissingEmailOnly(e.target.checked)}
            />
            Only agents missing an email
          </label>

          {agents.isLoading ? (
            <Skeleton className="h-40 w-full" />
          ) : rows.length === 0 ? (
            <EmptyState
              title="No agents match"
              description={
                search || escalated !== "" || missingEmailOnly
                  ? truncated
                    ? `Nothing in the first ${loaded.length} of ${total} agents matches. Narrow the search to reach the rest.`
                    : "Nothing matches these filters."
                  : "No listing agents recorded yet. Adding one here, or pasting a listing that names one, creates the shared record."
              }
            />
          ) : (
            <div className="min-w-0">
              {/*
                🔴 `SubTable`'s own search and sort are OFF here, by giving it a
                threshold this list can never cross. Its chrome would act on the
                page of 100 in hand, and the reader already has a SERVER-side
                search box above — a second one filtering the first one's
                results would be two boxes that disagree about what they searched.
                The truncation caption below says which is which.
              */}
              <SubTable<ListingAgentDto>
                caption="Every listing agent on file, with their firm and contact details"
                data-testid="listing-agents-table"
                rows={rows}
                rowKey={(a) => a.id}
                columns={agentColumns(setEditing, canViewDeals ? setDealsFor : undefined)}
                chromeThreshold={Number.POSITIVE_INFINITY}
              />
              {truncated && (
                <p className="text-caption text-gray-mid mt-space-2 m-0">
                  Showing the first {loaded.length} of {total} agents. Search to
                  reach the rest — the missing-email filter only searches what is
                  shown.
                </p>
              )}
            </div>
          )}
        </div>
      </Modal>

      <ListingAgentEditModal
        open={creating || editing !== null}
        agent={editing}
        onClose={() => {
          setCreating(false);
          setEditing(null);
        }}
        // X5: from the directory, USE THIS AGENT means "that's the one I meant"
        // — the New agent form becomes that agent's own Edit form, so whatever
        // the specialist came to change is one step away. Nothing is written.
        onUseExisting={(existing) => {
          setCreating(false);
          setEditing(existing);
        }}
      />

      <WhosCallingPanel
        open={dealsFor !== null}
        onClose={() => setDealsFor(null)}
        agent={dealsFor ? { id: dealsFor.id, label: dealsFor.displayName } : undefined}
      />
    </>
  );
}

type EscalatedFilter = "" | "yes" | "no";

/**
 * No email on file.
 *
 * <p>The one agent field that is not merely nice to have. `upsertAgent`
 * get-or-creates by email, so an agent without one cannot be matched: the
 * dedupe probe is skipped entirely and every subsequent paste naming them
 * creates a duplicate record with its own listing count. Shown wherever an
 * agent is read, so the gap is fixable rather than merely present.</p>
 */
export function hasNoEmail(agent: Pick<ListingAgentDto, "email">): boolean {
  return !agent.email?.trim();
}

export function MissingEmailChip() {
  return (
    <StatusBadge
      tone="warn"
      hint="Agents are matched by email. Without one, pasting a listing that names this agent creates a duplicate record instead of finding this one."
    >
      No email on file
    </StatusBadge>
  );
}

/**
 * The directory row.
 *
 * 🔴 THE ROWS STAY RICH. An agent's identity cell carries the recruitment badge
 * and their title, and the contact cell carries the missing-email chip that is
 * the whole reason this screen exists — the chip is a WORKFLOW STATE with a real
 * consequence (a listing pasted for an agent with no email creates a duplicate
 * record instead of finding this one), which is why it is a marked chip rather
 * than the plain em dash a merely-absent value would get.
 */
function agentColumns(
  onEdit: (agent: ListingAgentDto) => void,
  /** Absent without Submissions.Read: the panel's endpoint needs both reads. */
  onViewDeals?: (agent: ListingAgentDto) => void,
): SubTableColumn<ListingAgentDto>[] {
  return [
    {
      key: "agent",
      header: "Agent",
      render: (a) => (
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-space-2">
            <span className="text-onyx">{a.displayName}</span>
            {a.escalatedToLeadId && <StatusBadge tone="info">In recruitment</StatusBadge>}
          </div>
          {a.title && <div className="text-caption text-gray-mid">{a.title}</div>}
        </div>
      ),
    },
    { key: "firm", header: "Firm", render: (a) => <TableValue value={a.firmName} /> },
    {
      key: "contact",
      header: "Contact",
      render: (a) => (
        <div className="min-w-0">
          {hasNoEmail(a) ? <MissingEmailChip /> : <div className="break-all">{a.email}</div>}
          {a.phone && <div className="text-caption text-gray-mid">{a.phone}</div>}
        </div>
      ),
    },
    {
      key: "listings",
      header: "Listings",
      align: "right",
      render: (a) => a.listingCount,
    },
    {
      key: "edit",
      // Empty rather than `sr-only`: the header is a real cell either way, and
      // an accessible name for a column of buttons repeats what each button
      // already says. Every row's control names its own agent.
      header: "",
      className: "text-right",
      render: (a) => (
        <div className="flex flex-wrap items-center justify-end gap-space-1">
          {onViewDeals && (
            <Button
              variant="text"
              size="sm"
              onClick={() => onViewDeals(a)}
              aria-label={`View deals for ${a.displayName}`}
            >
              View deals
            </Button>
          )}
          <Button
            variant="text"
            size="sm"
            onClick={() => onEdit(a)}
            aria-label={`Edit ${a.displayName}`}
          >
            <Pencil size={13} strokeWidth={1.5} aria-hidden /> Edit
          </Button>
        </div>
      ),
    },
  ];
}

/**
 * The New agent form, and the editor for one shared agent record.
 *
 * <p>ONE form for every place a listing agent is created — the directory, the
 * listing's Edit listing agent modal and NEW LISTING — so the duplicate warning
 * and the create rules cannot differ between them.</p>
 *
 * <p>🔴 <b>Creating is STRICT and checks first.</b> Pressing Save on a new agent
 * runs the duplicate check before anything is written. If agents already on
 * file look like this one, the form shows them ("Already on file?") and writes
 * nothing. The create itself always sends <code>rejectExisting</code>, so it
 * either inserts a new row or refuses with a 409 — it never reuses, fills in or
 * merges an existing agent behind the specialist's back. A 409 (another request
 * added the same details a moment earlier) re-runs the check and shows the
 * match.</p>
 *
 * <p><b>USE THIS AGENT writes nothing.</b> It hands the existing record to the
 * host and discards what was typed; the existing agent's details are not
 * changed. What the host does with it is the host's: a listing modal selects
 * it (SAVE still required), the directory opens that agent's Edit form.</p>
 *
 * <p>Editing an existing agent is unchanged: <code>updateAgent</code> (PATCH by
 * id, null=unchanged / ""=clear).</p>
 *
 * <p><b>PASTE TO FILL</b> (D1a, D5) sits at the top of the New agent form. A
 * paste fills BLANK fields only, each marked "from paste" (or "read from image
 * — check it") until edited; a field the specialist typed is never overwritten
 * and a disagreement is shown for them to settle. Values with no field are
 * offered for Notes. Nothing saves until SAVE AGENT, through the same check and
 * strict create. <code>initialPaste</code> opens the form already filled from a
 * paste — and, when the host has already run the duplicate check on it, on the
 * "Already on file?" panel exactly as Save would show it.</p>
 */
export function ListingAgentEditModal({
  open,
  agent,
  onClose,
  onSaved,
  onUseExisting,
  initialPaste,
}: {
  open: boolean;
  agent?: ListingAgentDto | null;
  onClose: () => void;
  /** Called with the agent that was created or updated. */
  onSaved?: (saved: ListingAgentDto) => void;
  /** Called by USE THIS AGENT. Omit it and the button is not offered. */
  onUseExisting?: (existing: ListingAgentDto) => void;
  /** CREATE mode only: open the form filled from this paste (read on opening). */
  initialPaste?: AgentPastePrefill | null;
}) {
  const qc = useQueryClient();
  const toast = useToast();
  const isEdit = Boolean(agent);

  // Keyed remount rather than a hydrate effect: reopening on a different agent
  // must not leave the previous one's values in the boxes.
  const [seed] = useState(() => seedAgentForm(agent, open ? initialPaste : null));
  const [form, setForm] = useState<AgentForm>(seed.form);
  const [check, setCheck] = useState<ListingAgentDuplicateCheckDto | null>(seed.check);
  const [paste, setPaste] = useState<PasteFormState>(seed.paste);
  const [raced, setRaced] = useState(false);
  // D19 — "Firm contact (no named agent)". Null until the specialist touches the
  // checkbox; until then it follows the form: ticked while there is no first or
  // last name, so a firm mailbox is flagged without anyone remembering to.
  const [firmContactChoice, setFirmContactChoice] = useState<boolean | null>(null);
  const [formKey, setFormKey] = useState(() => agent?.id ?? "new");
  const currentKey = agent?.id ?? "new";
  if (currentKey !== formKey) {
    setFormKey(currentKey);
    setForm(agentFormFrom(agent));
    setCheck(null);
    setPaste(NO_PASTE);
    setRaced(false);
    setFirmContactChoice(null);
  }

  // A closed form forgets what it found, so the next New agent starts blank —
  // or, opened with a paste, starts from that paste.
  const [wasOpen, setWasOpen] = useState(open);
  if (open !== wasOpen) {
    setWasOpen(open);
    const next = seedAgentForm(agent, open ? initialPaste : null);
    setForm(next.form);
    setCheck(next.check);
    setPaste(next.paste);
    setRaced(false);
    setFirmContactChoice(null);
  }

  const firmContact = isEdit
    ? Boolean(agent?.isFirmContact)
    : (firmContactChoice ?? firmContactByDefault(form));

  const errors = agentFormErrors(form);
  const canSave = !errors.email && !errors.phone && agentHasIdentity(form);
  const showingMatches = !isEdit && check !== null && check.matches.length > 0;

  const changeForm = (next: AgentForm) => {
    setPaste((p) => pasteAfterEdit(form, next, p));
    setForm(next);
    // The matches describe the details that were checked. Once they change, the
    // next Save checks again.
    setCheck(null);
    setRaced(false);
  };

  // A paste, a "Use pasted" or "Add to notes" changes the form as an edit
  // does — the check describes the old details — but keeps its own markers.
  const replaceForm = (next: AgentForm, nextPaste: PasteFormState) => {
    setForm(next);
    setPaste(nextPaste);
    setCheck(null);
    setRaced(false);
  };

  const applyPaste = (choice: AgentPasteChoice) => {
    const merged = pasteIntoForm(form, choice, paste);
    replaceForm(merged.form, merged.paste);
  };

  const save = useMutation({
    mutationFn: async (mode: "check-then-create" | "create-new" | "update") => {
      if (mode === "update") {
        if (!agent) throw new Error("No agent to update.");
        const saved = await listingsApi.updateAgent(
          agent.id,
          agentToPayload(form, { forEdit: true }),
        );
        return { kind: "saved" as const, saved };
      }
      const payload: UpsertListingAgentPayload = {
        ...agentToPayload(form, { forEdit: false }),
        isFirmContact: firmContact,
        rejectExisting: true,
      };
      if (mode === "check-then-create") {
        const found = await listingsApi.checkAgentDuplicates(payload);
        if (found.matches.length > 0) return { kind: "matches" as const, check: found, raced: false };
      }
      try {
        const saved = await listingsApi.upsertAgent(payload);
        return { kind: "saved" as const, saved };
      } catch (e) {
        if (!isListingAgentAlreadyOnFile(e)) throw e;
        // Someone added these details since the check, or the check missed a
        // reuse the create found. Show which agent it was.
        const found = await listingsApi.checkAgentDuplicates(payload);
        if (found.matches.length === 0) throw e;
        return { kind: "matches" as const, check: found, raced: true };
      }
    },
    onSuccess: (result) => {
      if (result.kind === "matches") {
        setCheck(result.check);
        setRaced(result.raced);
        return;
      }
      // The whole listing prefix: an agent's name and firm are denormalised
      // onto every listing row and detail that renders them, so a rename that
      // refreshed only the agent list would leave stale names on screen for
      // the full 5-minute staleTime.
      void qc.invalidateQueries({ queryKey: listingKeys.all });
      if (isEdit) {
        // Q8: a corrected email changes who a submission's broker intro goes to
        // and what the escalation gate reads, and both live under the
        // submission prefix.
        void qc.invalidateQueries({ queryKey: buyerSubmissionKeys.all });
      }
      toast.success(isEdit ? "Agent updated." : "Agent created.");
      onSaved?.(result.saved);
      onClose();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <Modal
      open={open}
      onClose={() => (save.isPending ? undefined : onClose())}
      title={isEdit ? `Edit ${agent?.displayName ?? "agent"}` : "New listing agent"}
      size="md"
      footer={
        <>
          <Button variant="text" onClick={onClose} disabled={save.isPending}>
            Cancel
          </Button>
          <Button
            disabled={save.isPending || !canSave || showingMatches}
            onClick={() => save.mutate(isEdit ? "update" : "check-then-create")}
            data-testid="listing-agent-form-save"
          >
            {save.isPending ? "Saving…" : "Save agent"}
          </Button>
        </>
      }
    >
      <div className="grid gap-space-3">
        {!isEdit && (
          <>
            <AgentPasteToFill onPick={applyPaste} disabled={save.isPending} />
            <PasteReview
              paste={paste}
              disabled={save.isPending}
              onUsePasted={(conflict) => {
                const chosen = choosePasted(form, conflict, paste);
                replaceForm(chosen.form, chosen.paste);
              }}
              onKeepTyped={(conflict) => setPaste((p) => keepTyped(conflict, p))}
              onAddNotes={(line) =>
                replaceForm({ ...form, notes: appendToNotes(form.notes, line) }, { ...paste, notesOffer: null })
              }
              onDismissNotes={() => setPaste((p) => ({ ...p, notesOffer: null }))}
            />
          </>
        )}
        {showingMatches && check && (
          <AgentAlreadyOnFilePanel
            check={check}
            raced={raced}
            disabled={save.isPending}
            onUseExisting={onUseExisting}
            onCreateNew={() => save.mutate("create-new")}
          />
        )}
        {!isEdit && !showingMatches && (
          <p className="text-caption text-gray-mid m-0">
            Agents are shared across listings. If the details you enter look like
            an agent already on file, we&apos;ll show you that agent before
            creating anything.
          </p>
        )}
        <AgentFieldsBlock
          value={form}
          onChange={changeForm}
          errors={errors}
          disabled={save.isPending}
          showPhoneType
          markers={isEdit ? undefined : markerTexts(paste)}
        />
        {isEdit ? (
          firmContact && (
            <p className="text-caption text-gray-mid m-0" data-testid="listing-agent-form-firm-contact-note">
              Firm contact (no named agent). This is set when the record is
              created and stays with it.
            </p>
          )
        ) : (
          <div className="grid gap-space-1">
            <label className="flex items-center gap-space-2 text-small">
              <input
                type="checkbox"
                checked={firmContact}
                disabled={save.isPending}
                onChange={(e) => {
                  setFirmContactChoice(e.target.checked);
                  setCheck(null);
                  setRaced(false);
                }}
                data-testid="listing-agent-form-firm-contact"
              />
              Firm contact (no named agent)
            </label>
            <p className="text-caption text-gray-mid m-0">
              A firm&apos;s general mailbox or main line. Firm contacts are never
              merged with a named agent.
            </p>
          </div>
        )}
        {!agentHasIdentity(form) && (
          <p className="text-caption text-gray-mid m-0">
            Give the agent a name, an email, or a phone number — a record with
            none of the three cannot be identified later.
          </p>
        )}
      </div>
    </Modal>
  );
}

/** A paste the New agent form opens from (D1b): the person chosen and, when the
 *  host has already run it, the duplicate check on the values the form will
 *  hold. Matches open the form on "Already on file?". */
export interface AgentPastePrefill {
  choice: AgentPasteChoice;
  duplicateCheck?: ListingAgentDuplicateCheckDto | null;
}

/** What the form starts from: the agent being edited, a paste for a new one,
 *  or blank. A paste is ignored in edit mode. */
export function seedAgentForm(
  agent: ListingAgentDto | null | undefined,
  prefill: AgentPastePrefill | null | undefined,
): { form: AgentForm; paste: PasteFormState; check: ListingAgentDuplicateCheckDto | null } {
  const base = agentFormFrom(agent);
  if (agent || !prefill) return { form: base, paste: NO_PASTE, check: null };
  const merged = pasteIntoForm(base, prefill.choice, NO_PASTE);
  const check = prefill.duplicateCheck && prefill.duplicateCheck.matches.length > 0 ? prefill.duplicateCheck : null;
  return { form: merged.form, paste: merged.paste, check };
}

/**
 * "Already on file?" — the agents the duplicate check found, shown before
 * anything is created.
 *
 * <p>CREATE NEW ANYWAY appears only when the server says a create would really
 * insert a row (<code>canCreateNew</code>). When it would not — the email, or
 * the name with the firm or phone, belongs to an agent on file — the panel
 * says so and offers only the existing agent.</p>
 */
export function AgentAlreadyOnFilePanel({
  check,
  raced,
  disabled,
  onUseExisting,
  onCreateNew,
}: {
  check: ListingAgentDuplicateCheckDto;
  raced: boolean;
  disabled: boolean;
  onUseExisting?: (existing: ListingAgentDto) => void;
  onCreateNew: () => void;
}) {
  return (
    <div
      className="grid gap-space-2 rounded-md border border-status-warning bg-white-warm px-space-3 py-space-3"
      role="status"
      data-testid="listing-agent-already-on-file"
    >
      <p className="text-small font-semibold text-onyx m-0">Already on file?</p>
      <p className="text-caption text-gray-mid m-0">
        {raced
          ? "An agent with these details was added a moment ago. These agents look like the one you're adding."
          : "These agents look like the one you're adding."}
      </p>
      <ul className="m-0 p-0 list-none grid gap-space-2">
        {check.matches.map((m) => (
          <li
            key={m.agent.id}
            className="flex flex-wrap items-start justify-between gap-space-2 rounded-md border border-border-light bg-white-pure px-space-3 py-space-2"
            data-testid={`listing-agent-match-${m.agent.id}`}
          >
            <span className="grid min-w-0 gap-[2px]">
              <AgentSummary agent={m.agent} />
              <span className="text-caption text-gray-mid">
                Matches on {describeMatchedOn(m.matchedOn)}
              </span>
            </span>
            {onUseExisting && (
              <Button
                type="button"
                variant="secondary"
                size="sm"
                disabled={disabled}
                onClick={() => onUseExisting(m.agent)}
                aria-label={`Use this agent: ${m.agent.displayName}`}
                data-testid={`listing-agent-use-${m.agent.id}`}
              >
                Use this agent
              </Button>
            )}
          </li>
        ))}
      </ul>
      {onUseExisting && (
        <p className="text-caption text-gray-mid m-0">
          Using an existing agent doesn&apos;t change their details.
        </p>
      )}
      {check.canCreateNew ? (
        <div>
          <Button
            type="button"
            variant="text"
            size="sm"
            disabled={disabled}
            onClick={onCreateNew}
            data-testid="listing-agent-create-anyway"
          >
            Create new anyway
          </Button>
        </div>
      ) : (
        <p className="text-caption text-gray-mid m-0" data-testid="listing-agent-cannot-create">
          A new agent can&apos;t be created with these details — they belong to an
          agent already on file. Use that agent, or change the details.
        </p>
      )}
    </div>
  );
}

function describeMatchedOn(fields: string[]): string {
  const words = fields.map((f) =>
    f === LISTING_AGENT_MATCH_FIELDS.email
      ? "email"
      : f === LISTING_AGENT_MATCH_FIELDS.phone
        ? "phone"
        : f === LISTING_AGENT_MATCH_FIELDS.name
          ? "name"
          : f,
  );
  if (words.length <= 1) return words[0] ?? "details";
  return `${words.slice(0, -1).join(", ")} and ${words[words.length - 1]}`;
}

// ---------------------------------------------------------------------------
// Shared agent field block — used by this editor (the one New agent form), the
// paste-to-create confirm step, and the Present-a-buyer wizard.
// ---------------------------------------------------------------------------

export interface AgentForm {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  /** D20. Edited only where `AgentFieldsBlock` is given `showPhoneType`. */
  phoneType: ListingAgentPhoneType;
  firmName: string;
  title: string;
  city: string;
  state: string;
  notes: string;
}

export const EMPTY_AGENT_FORM: AgentForm = {
  firstName: "",
  lastName: "",
  email: "",
  phone: "",
  phoneType: "Unknown",
  firmName: "",
  title: "",
  city: "",
  state: "",
  notes: "",
};

export function agentFormFrom(
  source?: ListingAgentDto | UpsertListingAgentPayload | null,
): AgentForm {
  if (!source) return EMPTY_AGENT_FORM;
  return {
    firstName: source.firstName ?? "",
    lastName: source.lastName ?? "",
    email: source.email ?? "",
    phone: source.phone ?? "",
    phoneType: source.phoneType ?? "Unknown",
    firmName: source.firmName ?? "",
    title: source.title ?? "",
    city: source.city ?? "",
    state: source.state ?? "",
    notes: source.notes ?? "",
  };
}

/** D19 — the Firm contact checkbox starts ticked when there is no first or
 *  last name; the specialist can untick it. */
export function firmContactByDefault(form: Pick<AgentForm, "firstName" | "lastName">): boolean {
  return !form.firstName.trim() && !form.lastName.trim();
}

/** A record with no name, email or phone cannot be found again — the one bar. */
export function agentHasIdentity(form: AgentForm): boolean {
  return Boolean(
    form.firstName.trim() ||
      form.lastName.trim() ||
      form.email.trim() ||
      form.phone.trim(),
  );
}

export function agentFormErrors(form: AgentForm) {
  return {
    email: emailFieldError(form.email, { required: false }),
    phone: phoneFieldError(form.phone, { required: false }),
  };
}

/**
 * `forEdit` chooses between the two payload conventions:
 * PATCH reads ""=clear, so a hydrated edit form sends every field; POST has
 * nothing to clear, so blanks are omitted entirely.
 */
export function agentToPayload(
  form: AgentForm,
  { forEdit }: { forEdit: boolean },
): UpsertListingAgentPayload {
  const v = (raw: string) => (forEdit ? raw.trim() : raw.trim() || undefined);
  return {
    firstName: v(form.firstName),
    lastName: v(form.lastName),
    email: v(form.email),
    phone: v(form.phone),
    // A create omits "Unknown" (the server's default), so the inline agent
    // forms that never show the field send exactly what they sent before.
    phoneType: forEdit || form.phoneType !== "Unknown" ? form.phoneType : undefined,
    firmName: v(form.firmName),
    title: v(form.title),
    city: v(form.city),
    state: v(form.state),
    notes: v(form.notes),
  };
}

export function AgentFieldsBlock({
  value,
  onChange,
  errors,
  disabled = false,
  showPhoneType = false,
  markers,
}: {
  value: AgentForm;
  onChange: (next: AgentForm) => void;
  errors: { email?: string; phone?: string };
  /**
   * PASTE TO FILL's per-field marker ("From paste" / "Read from image — check
   * it"), shown under the field until it is edited. Only the New agent form
   * passes it; every other host renders exactly as before.
   */
  markers?: Partial<Record<keyof AgentForm, string>>;
  /**
   * D20 — render the Phone type select beside Phone. Only the New / Edit agent
   * form shows it; the paste-to-create and Present-a-buyer inline forms keep
   * their fields as they were.
   */
  showPhoneType?: boolean;
  /**
   * Locks every field. The New agent form sets it while its check or create is
   * running, so what that request describes is still what is on screen when it
   * answers.
   */
  disabled?: boolean;
}) {
  const set = <K extends keyof AgentForm>(key: K, next: AgentForm[K]) =>
    onChange({ ...value, [key]: next });

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-space-3">
      <FormField label="First name" helperText={markers?.firstName}>
        <Input value={value.firstName} disabled={disabled} onChange={(e) => set("firstName", e.target.value)} />
      </FormField>
      <FormField label="Last name" helperText={markers?.lastName}>
        <Input value={value.lastName} disabled={disabled} onChange={(e) => set("lastName", e.target.value)} />
      </FormField>
      <FormField label="Email" error={errors.email} helperText={markers?.email}>
        <Input
          type="email"
          value={value.email}
          disabled={disabled}
          invalid={Boolean(errors.email)}
          onChange={(e) => set("email", e.target.value)}
        />
      </FormField>
      <FormField label="Phone" error={errors.phone} helperText={markers?.phone}>
        <Input
          type="tel"
          value={value.phone}
          disabled={disabled}
          invalid={Boolean(errors.phone)}
          onChange={(e) => set("phone", e.target.value)}
        />
      </FormField>
      {showPhoneType && (
        <FormField label="Phone type" helperText={markers?.phoneType}>
          <Select
            value={value.phoneType}
            disabled={disabled}
            onChange={(e) => set("phoneType", e.target.value as ListingAgentPhoneType)}
            data-testid="listing-agent-form-phone-type"
          >
            {LISTING_AGENT_PHONE_TYPES.map((t) => (
              <option key={t.value} value={t.value}>
                {t.label}
              </option>
            ))}
          </Select>
        </FormField>
      )}
      <FormField label="Firm" helperText={markers?.firmName}>
        <Input value={value.firmName} disabled={disabled} onChange={(e) => set("firmName", e.target.value)} />
      </FormField>
      <FormField label="Title" helperText={markers?.title}>
        <Input value={value.title} disabled={disabled} onChange={(e) => set("title", e.target.value)} />
      </FormField>
      <FormField label="City" helperText={markers?.city}>
        <Input value={value.city} disabled={disabled} onChange={(e) => set("city", e.target.value)} />
      </FormField>
      <FormField label="State" helperText={markers?.state}>
        <Select value={value.state} disabled={disabled} onChange={(e) => set("state", e.target.value)}>
          <option value="">—</option>
          {US_STATES.map((s) => (
            <option key={s.code} value={s.code}>
              {s.name} ({s.code})
            </option>
          ))}
        </Select>
      </FormField>
      <FormField label="Notes" className="sm:col-span-2">
        <Textarea rows={3} value={value.notes} disabled={disabled} onChange={(e) => set("notes", e.target.value)} />
      </FormField>
    </div>
  );
}
